import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../services/api';

export interface TableComparison {
  table_name: string;
  // Spec 298 FR-004/FR-006: null when a count is unknown, or there were no backed-up rows to measure against
  backup_row_count: number | null;
  current_row_count: number | null;
  row_count_diff: number | null;
  row_count_diff_pct: number | null;
  schema_changed: boolean;
  schema_changes?: SchemaChange[];
}

export interface SchemaChange {
  change_type: 'column_added' | 'column_removed' | 'column_modified' | 'type_changed';
  column_name: string;
  old_value?: string;
  new_value?: string;
  description: string;
}

export interface DeltaCompareResult {
  lakehouse_id: string;
  lakehouse_name: string;
  backup_run_id: string;
  backup_timestamp: string;
  comparison_timestamp: string;
  tables_compared: number;
  tables_with_changes: number;
  total_row_diff: number;
  tables: TableComparison[];
  summary: {
    tables_added: number;
    tables_removed: number;
    tables_modified: number;
    total_backup_rows: number;
    /** Null when there is no current snapshot to count. */
    total_current_rows: number | null;
  };
  // Set when the lakehouse has no current table snapshot (Spec 184 FR-003 degrade).
  currentSnapshotMissing?: boolean;
}

export interface AvailableBackup {
  id: string;
  started_at: string;
  completed_at: string;
  backup_type: string;
  tables_backed_up: number;
  total_size_bytes: number;
}

/** Summary row for a persisted comparison run (Spec 184 FR-009). */
export interface DeltaCompareRunSummary {
  id: string;
  lakehouse_id: string;
  backup_run_id: string;
  comparison_timestamp: string;
  tables_compared: number;
  tables_with_changes: number;
  total_row_diff: number;
}

export interface CompareBackupRequest {
  lakehouseId: string;
  backupRunId: string;
  tableFilter?: string[];
}

/**
 * Hook to fetch available backups for comparison
 */
export function useAvailableBackups(
  lakehouseId: string | undefined,
  options?: Omit<UseQueryOptions<AvailableBackup[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<AvailableBackup[]>({
    queryKey: ['lakehouses', lakehouseId, 'available-backups'],
    queryFn: async () => {
      if (!lakehouseId) {
        throw new Error('Lakehouse ID is required');
      }
      const response = await apiClient.get<{ success: boolean; data: AvailableBackup[] }>(
        `/api/v1/delta-compare/backups`,
        { lakehouseId }
      );
      return response.data;
    },
    enabled: !!lakehouseId,
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

/**
 * Hook to compare backup with current lakehouse state
 */
export function useCompareBackup() {
  const queryClient = useQueryClient();

  return useMutation<DeltaCompareResult, Error, CompareBackupRequest>({
    mutationFn: async (request: CompareBackupRequest) => {
      const response = await apiClient.post<{ success: boolean; data: DeltaCompareResult }>(
        `/api/v1/delta-compare/compare`,
        {
          lakehouseId: request.lakehouseId,
          backupRunId: request.backupRunId,
          tableFilter: request.tableFilter,
        }
      );
      return response.data;
    },
    onSuccess: (result) => {
      // Invalidate the persisted-runs list so the new run shows under "Past comparisons".
      queryClient.invalidateQueries({ queryKey: ['delta-compare', 'runs', result.lakehouse_id] });
    },
    onError: (error) => {
      console.error('Failed to compare backup:', error.message);
    },
  });
}

/**
 * Hook to list persisted comparison runs for a lakehouse (FR-009).
 */
export function useDeltaCompareRuns(
  lakehouseId: string | undefined,
  options?: Omit<UseQueryOptions<DeltaCompareRunSummary[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DeltaCompareRunSummary[]>({
    queryKey: ['delta-compare', 'runs', lakehouseId],
    queryFn: async () => {
      if (!lakehouseId) throw new Error('Lakehouse ID is required');
      const response = await apiClient.get<{ success: boolean; data: DeltaCompareRunSummary[] }>(
        `/api/v1/delta-compare/runs`,
        { lakehouseId }
      );
      return response.data;
    },
    enabled: !!lakehouseId,
    staleTime: 30000,
    ...options,
  });
}

/**
 * Hook to re-open a persisted comparison run (FR-009).
 */
export function useDeltaCompareRun(
  runId: string | undefined,
  options?: Omit<UseQueryOptions<DeltaCompareResult>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DeltaCompareResult>({
    queryKey: ['delta-compare', 'run', runId],
    queryFn: async () => {
      if (!runId) throw new Error('Run ID is required');
      const response = await apiClient.get<{ success: boolean; data: DeltaCompareResult }>(
        `/api/v1/delta-compare/runs/${runId}`
      );
      return response.data;
    },
    enabled: !!runId,
    staleTime: 30000,
    ...options,
  });
}
